import Bugsnag from '@bugsnag/browser';
import { markExternalAuthChallengeShown, triggerExternalAuthStatus } from '@src/features/payments/store/payment';
import type { Transactable } from '@src/domains/transactables';
import { parsePhoneNumber } from '@src/common/phoneParsing';
import {
  ExternalAuthenticationProvider,
  ExternalAuthenticationProviderParams,
  ExternalAuthResult,
} from '@src/features/payments/external-auth/types';
import { CountryCode, findCountryByAlpha3Code } from '@src/common/auth';
import { PAGARME_3DS_STATUS, PAGARME_3DS_CHALLENGE_WINDOW_SIZE } from './constants';
import { Observe3dsChallengeElementParams, WindowWithScript3ds } from './types';
import { Address } from '@src/core/buyerApiClient';
import { Dispatch } from '@reduxjs/toolkit';

declare let __PAGARME_3DS_URL__: string;

// TODO: Backend can't accept countryCode yet, remove AddressWithCountryCode when backend is updated
type AddressWithCountryCode = Address & {
  countryCode?: string;
};

export class PagarmeProvider implements ExternalAuthenticationProvider {
  private mutationObserver: MutationObserver | null = null;
  private observerTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly OBSERVER_TIMEOUT_MS = 120000; // 2 minutes as fallback security

  private observe3dsChallengeElement(params: Observe3dsChallengeElementParams): void {
    const { onFail, onSuccess, onChallengeShown } = params;

    this.cleanupObserver();

    const targetNode = document.querySelector('#challengeContainer') as Node;
    const challengeWrapperElement = document.querySelector('#challenge3dsWrapper') as HTMLDivElement;

    if (!targetNode) {
      console.warn('[Pagarme 3DS] challengeContainer not found, observer will not be created');

      this.cleanupObserver();
      this.hideModal();
      onFail?.();

      return;
    }

    const observer = new MutationObserver((mutationList) => {
      for (const mutation of mutationList) {
        for (const added_node of mutation.addedNodes) {
          const isChallengeIframeAdded = (added_node as HTMLElement)?.getAttribute('name') === 'challengeIframe';

          if (isChallengeIframeAdded) {
            if (challengeWrapperElement) challengeWrapperElement.style.display = 'block';

            onChallengeShown?.();

            // Clean up timeout when iframe is added (script worked)
            if (this.observerTimeoutId) {
              clearTimeout(this.observerTimeoutId);
              this.observerTimeoutId = null;
            }
          }
        }

        for (const removed_node of mutation.removedNodes) {
          const isChallengeIframeRemoved = (removed_node as HTMLElement)?.getAttribute('name') === 'challengeIframe';

          if (isChallengeIframeRemoved) {
            this.hideModal();
            this.cleanupObserver();
          }
        }
      }
    });

    this.mutationObserver = observer;
    observer.observe(targetNode, { subtree: false, childList: true });

    // Security timeout: disconnect observer if the script doesn't load in 2 minutes
    this.observerTimeoutId = setTimeout(() => {
      console.warn('[Pagarme 3DS] MutationObserver timeout: script 3DS may not have loaded');
      this.cleanupObserver();
      this.hideModal();
      onFail?.();
    }, this.OBSERVER_TIMEOUT_MS);

    onSuccess?.();
  }

  private sendBugsnagNotification(data: Record<string, unknown> | undefined, dispatch: Dispatch): void {
    if (data) {
      const transStatus = data['trans_status'] as string;
      dispatch(triggerExternalAuthStatus(transStatus));

      switch (transStatus) {
        case PAGARME_3DS_STATUS.success:
        case PAGARME_3DS_STATUS.auth_try:
          if (!('tds_server_trans_id' in data) || !data['tds_server_trans_id']) {
            Bugsnag.notify(
              {
                name: 'ExternalAuthStatus',
                message: `3DS returned successful status but no tds_server_trans_id was found`,
              },
              (event) => {
                event.addMetadata('data_3ds', data);
              },
            );
          }
          break;
        case PAGARME_3DS_STATUS.failure:
        case PAGARME_3DS_STATUS.denied:
          Bugsnag.notify(
            {
              name: 'ExternalAuthStatusPagarmeFailure',
              message: `3DS returned failure status`,
            },
            (event) => {
              event.addMetadata('data_3ds', data);
            },
          );
          break;
        case PAGARME_3DS_STATUS.challenge:
        case PAGARME_3DS_STATUS.unavailable:
        default:
          Bugsnag.notify(
            {
              name: 'ExternalAuthStatusPagarmeServerError',
              message: '3DS com status desconhecido',
            },
            (event) => {
              event.addMetadata('data_3ds', data);
            },
          );
          break;
      }
    } else {
      Bugsnag.notify(
        {
          name: 'ExternalAuthPagarmeNoData',
          message: 'No data returned from 3DS',
        },
        (event) => {
          event.addMetadata('external_authentication', {
            data,
          });
        },
      );
    }
  }

  private hideModal(): void {
    const challengeWrapperElement = document.querySelector('#challenge3dsWrapper') as HTMLDivElement;
    if (challengeWrapperElement) {
      challengeWrapperElement.style.display = 'none';
    }
  }

  private cleanupObserver(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    if (this.observerTimeoutId) {
      clearTimeout(this.observerTimeoutId);
      this.observerTimeoutId = null;
    }
  }

  async handleAuthentication(params: ExternalAuthenticationProviderParams): Promise<ExternalAuthResult> {
    const { transactable, data, dispatch, paymentOption, paymentMethodExternalAuthentication, buyer } = params;
    const { order, sale, directLink } = transactable;

    if (!order && !sale && !directLink) {
      return {
        proceed: false,
        error: 'tds_invalid_transactable',
        externalAuthentication: undefined,
      };
    }

    const wdw = window as WindowWithScript3ds;
    const { body } = document;
    const url3dsScript = __PAGARME_3DS_URL__;
    const script = document.createElement('script');
    script.src = url3dsScript;

    const handleReturn = (safeDone: (r: ExternalAuthResult) => boolean, data?: Record<string, unknown>): void => {
      const result: ExternalAuthResult = {
        proceed: false,
        error: undefined,
        externalAuthentication: data,
        externalError: undefined,
      };

      if (data) {
        switch (data['trans_status']) {
          case PAGARME_3DS_STATUS.success:
          case PAGARME_3DS_STATUS.auth_try:
            result.proceed = true;
            result.externalAuthentication = data;
            result.externalError = undefined;
            break;

          case PAGARME_3DS_STATUS.failure:
            result.error = 'tds_transaction_failed';
            result.proceed = false;
            result.externalError = (data['error'] as string) ?? undefined;
            break;
          case PAGARME_3DS_STATUS.denied:
            result.error = 'tds_transaction_denied';
            result.proceed = false;
            result.externalError = (data['error'] as string) ?? undefined;
            break;

          case PAGARME_3DS_STATUS.challenge:
          case PAGARME_3DS_STATUS.unavailable:
          default:
            result.proceed = false;
            result.error = 'tds_generic_error';
            result.externalError = (data['error'] as string) ?? undefined;
            break;
        }
      } else {
        result.error = 'tds_generic_error';
      }

      const wasResolved = safeDone(result);
      if (wasResolved) {
        // Promise was already resolved (e.g., by timeout)
        // skip all side effects to avoid misleading analytics and error reports
        return;
      }

      // Only execute side effects if we successfully resolved the promise
      if (body.contains(script)) {
        body.removeChild(script);
      }

      this.sendBugsnagNotification(data, dispatch);

      this.cleanupObserver();
      this.hideModal();
    };

    return new Promise((done) => {
      let isPromiseResolved = false;
      const safeDone = (result: ExternalAuthResult): boolean => {
        if (isPromiseResolved) return true;
        isPromiseResolved = true;
        done(result);
        return false;
      };

      const tdsToken = paymentMethodExternalAuthentication.data['tds_token'];
      if (!tdsToken) {
        safeDone({
          proceed: false,
          error: 'tds_generic_error',
          externalAuthentication: undefined,
          externalError: undefined,
        });

        Bugsnag.notify(
          {
            name: 'ExternalAuthPagarmeTokenNotFound',
            message: '3DS token not found',
          },
          (event) => {
            event.addMetadata('external_authentication', {
              externalAuthentication: paymentMethodExternalAuthentication,
            });
          },
        );
        return;
      }

      const billingAddressCountry = findCountryByAlpha3Code(
        (data?.paymentCard?.billingAddress as AddressWithCountryCode)?.countryCode ?? CountryCode.BRAZIL,
      );
      const country = findCountryByAlpha3Code(buyer?.countryCode ?? CountryCode.BRAZIL);
      const { countryCode, phoneWithoutCountryCode, isValid } = parsePhoneNumber(buyer?.phone, country?.alpha2);

      if (!isValid) {
        Bugsnag.notify({
          name: 'ExternalAuthPhoneNumberInvalid',
          message: `Phone number ${buyer?.phone} is invalid`,
        });
      }

      const customerAddress = {
        country: billingAddressCountry?.alpha2 ?? country?.alpha2,
        state: data?.paymentCard?.billingAddress?.state,
        city: data?.paymentCard?.billingAddress?.city,
        zip_code: data?.paymentCard?.billingAddress?.zipCode,
        line_1: `${data?.paymentCard?.billingAddress?.number ?? 'SN'}, ${data?.paymentCard?.billingAddress?.street}, ${
          data?.paymentCard?.billingAddress?.district
        }`,
        line_2: data?.paymentCard?.billingAddress?.complement ?? 'N/A',
      };

      const orderData = {
        payments: [
          {
            payment_method: paymentOption.paymentMethod.code,
            credit_card: {
              card: {
                number: data.paymentCard?.cardNumber,
                holder_name: data.paymentCard?.holder,
                exp_month: data.paymentCard?.validityMonth?.padStart(2, '0'),
                exp_year: data.paymentCard?.validityYear,
                billing_address: customerAddress,
              },
            },
            amount: this.getPurchaseData(transactable).amount,
          },
        ],
        customer: {
          name: buyer?.fullName,
          email: buyer?.email,
          code: buyer?.id,
          document: buyer?.identity,
          phones: {
            mobile_phone: {
              country_code: countryCode,
              area_code: phoneWithoutCountryCode?.slice(0, 2),
              number: phoneWithoutCountryCode?.slice(2),
            },
          },
        },
        shipping: {
          recipient_name: buyer?.fullName,
          address: customerAddress,
        },
      };

      script.onerror = () => {
        console.warn('[Pagarme 3DS] Error loading 3DS script');

        if (body.contains(script)) {
          body.removeChild(script);
        }

        // Clean up observer if the script doesn't load and hide modal
        this.cleanupObserver();
        this.hideModal();

        safeDone({
          proceed: false,
          error: 'tds_generic_error',
          externalAuthentication: undefined,
          externalError: undefined,
        });

        Bugsnag.notify(
          {
            name: 'ExternalAuthPagarmeScriptLoadError',
            message: 'Error loading 3DS script',
          },
          (event) => {
            event.addMetadata('script_load_error', {
              url: url3dsScript,
            });
          },
        );
      };

      script.onload = () => {
        try {
          if (!wdw.TDS || typeof wdw.TDS.init !== 'function') {
            throw new Error('TDS object or TDS.init method is not available');
          }

          wdw.TDS.init(
            {
              token: tdsToken,
              tds_method_container_element: document.getElementById('tdsMethodContainer'),
              challenge_container_element: document.getElementById('challengeContainer'),
              use_default_challenge_iframe_style: true,
              challenge_window_size: PAGARME_3DS_CHALLENGE_WINDOW_SIZE.FULL_SCREEN,
            },
            orderData,
          )
            .then((response) => {
              if (response.length > 0) {
                // Pagarme returns an array of responses for each card,
                // we only have one card, so we take the first item
                handleReturn(safeDone, response[0]);
                return;
              }

              handleReturn(safeDone, undefined);
            })
            .catch((error) => {
              console.warn('[Pagarme 3DS] Error during 3DS authentication:', error);

              if (body.contains(script)) {
                body.removeChild(script);
              }

              // Clean up observer if there is an error and hide modal
              this.cleanupObserver();
              this.hideModal();

              safeDone({
                proceed: false,
                error: 'tds_generic_error',
                externalAuthentication: undefined,
                externalError: undefined,
              });

              Bugsnag.notify(
                {
                  name: 'ExternalAuthPagarmeInit3dsError',
                  message: 'Error initializing 3DS',
                },
                (event) => {
                  event.addMetadata('error_init_3ds', {
                    error: error,
                  });
                },
              );
            });
        } catch (error) {
          console.warn('[Pagarme 3DS] Synchronous error initializing 3DS:', error);

          if (body.contains(script)) {
            body.removeChild(script);
          }

          // Clean up observer if there is a synchronous error and hide modal
          this.cleanupObserver();
          this.hideModal();

          safeDone({
            proceed: false,
            error: 'tds_generic_error',
            externalAuthentication: undefined,
            externalError: undefined,
          });

          Bugsnag.notify(
            {
              name: 'ExternalAuthPagarmeInit3dsError',
              message: 'Synchronous error initializing 3DS',
            },
            (event) => {
              event.addMetadata('error_init_3ds', {
                error: error,
              });
            },
          );
        }
      };

      this.observe3dsChallengeElement({
        onFail: () => {
          if (body.contains(script)) {
            body.removeChild(script);
          }

          safeDone({
            proceed: false,
            error: 'tds_generic_error',
            externalAuthentication: undefined,
            externalError: undefined,
          });
        },
        onSuccess: () => {
          body.appendChild(script);
        },
        onChallengeShown: () => dispatch(markExternalAuthChallengeShown()),
      });
    });
  }

  private getPurchaseData(transactable: Transactable) {
    if (transactable.order) {
      return {
        amount: transactable.order.finalAmountCents,
        date: transactable.order.createdAt,
      };
    }

    if (transactable.sale) {
      return {
        amount: transactable.sale.totalAmountCents,
        date: transactable.sale.createdAt,
      };
    }

    if (transactable.directLink) {
      return {
        amount: transactable.directLink.totalAmountCents,
        date: transactable.directLink.createdAt,
      };
    }

    return {
      amount: 0,
      date: new Date().toISOString(),
    };
  }
}
